import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import ModuleLayout from '../common/ModuleLayout';

const { FiLayers, FiPlus, FiTrash2 } = FiIcons;

const ModuleTwo = ({ data, onSave, completed }) => {
  const [bookType, setBookType] = useState('nonfiction');
  const [fictionData, setFictionData] = useState({ act1: '', act2: '', act3: '', act4: '', notes: '' });
  const [nonfictionChapters, setNonfictionChapters] = useState([{ chapter: 1, bigIdea: '', biblicalTruth: '', readerShift: '', keyTakeaway: '' }]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (data.bookType) setBookType(data.bookType);
    if (data.fictionData) setFictionData(data.fictionData);
    if (data.nonfictionChapters) setNonfictionChapters(data.nonfictionChapters);
  }, [data]);

  const handleFictionChange = (field, value) => {
    setFictionData(prev => ({ ...prev, [field]: value }));
  };

  const handleChapterChange = (index, field, value) => {
    setNonfictionChapters(prev => prev.map((chapter, i) => i === index ? { ...chapter, [field]: value } : chapter));
  };

  const addChapter = () => {
    setNonfictionChapters(prev => [...prev, { chapter: prev.length + 1, bigIdea: '', biblicalTruth: '', readerShift: '', keyTakeaway: '' }]);
  };

  const removeChapter = (index) => {
    if (nonfictionChapters.length > 1) {
      setNonfictionChapters(prev => prev.filter((_, i) => i !== index).map((chapter, i) => ({ ...chapter, chapter: i + 1 })));
    }
  };

  const handleSave = async (markComplete) => {
    setIsLoading(true);
    const dataToSave = { bookType, fictionData, nonfictionChapters };
    setTimeout(() => {
      onSave(dataToSave, markComplete);
      setIsLoading(false);
    }, 800);
  };

  // Improved completion check
  const canComplete = bookType === 'fiction'
    ? Object.values(fictionData).some(v => v && v.trim() !== '')
    : nonfictionChapters.every(ch => ch.bigIdea.trim() !== '' && ch.biblicalTruth.trim() !== '');

  const acts = [
    { name: 'Act 1: Setup', purpose: 'Introduce character, world, and inciting incident' },
    { name: 'Act 2A: Rising Action', purpose: 'Develop conflict and obstacles' },
    { name: 'Act 2B: Midpoint Crisis', purpose: 'Major revelation or setback' },
    { name: 'Act 3: Resolution', purpose: 'Climax and resolution' }
  ];

  return (
    <ModuleLayout
      title="Structure the Story or Message"
      description="Create a clear roadmap for your book — fiction or nonfiction — so momentum replaces overwhelm."
      icon={FiLayers}
      onSave={handleSave}
      completed={completed}
      canComplete={canComplete}
      isLoading={isLoading}
    >
      <div className="space-y-8">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Book Type</h3>
          <div className="flex space-x-4">
            {['fiction', 'nonfiction'].map(type => (
              <label key={type} className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  value={type}
                  checked={bookType === type}
                  onChange={(e) => setBookType(e.target.value)}
                  className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="ml-2 text-gray-700 capitalize font-medium">{type}</span>
              </label>
            ))}
          </div>
        </div>

        {bookType === 'fiction' ? (
          <div className="space-y-6">
            {acts.map((act, index) => (
              <div key={index} className="bg-white rounded-xl shadow-sm p-6 border border-slate-100">
                <h4 className="text-lg font-bold text-slate-800 mb-1">{act.name}</h4>
                <p className="text-sm text-slate-500 mb-4">{act.purpose}</p>
                <textarea
                  value={fictionData[`act${index + 1}`] || ''}
                  onChange={(e) => handleFictionChange(`act${index + 1}`, e.target.value)}
                  rows={4}
                  className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all resize-none"
                  placeholder="Outline this act..."
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-6">
            {nonfictionChapters.map((chapter, index) => (
              <div key={index} className="bg-white rounded-xl shadow-sm p-6 border border-slate-100">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-bold text-slate-800">Chapter {chapter.chapter}</h4>
                  {nonfictionChapters.length > 1 && (
                    <button onClick={() => removeChapter(index)} className="text-red-400 hover:text-red-600 p-2 transition-colors">
                      <FiTrash2 />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Big Idea</label>
                    <input
                      type="text"
                      value={chapter.bigIdea}
                      onChange={(e) => handleChapterChange(index, 'bigIdea', e.target.value)}
                      className="w-full p-3 bg-slate-50 border border-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                      placeholder="The main concept"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Biblical Truth</label>
                    <input
                      type="text"
                      value={chapter.biblicalTruth}
                      onChange={(e) => handleChapterChange(index, 'biblicalTruth', e.target.value)}
                      className="w-full p-3 bg-slate-50 border border-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                      placeholder="Scripture or principle"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Key Takeaway</label>
                    <input
                      type="text"
                      value={chapter.keyTakeaway}
                      onChange={(e) => handleChapterChange(index, 'keyTakeaway', e.target.value)}
                      className="w-full p-3 bg-slate-50 border border-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                      placeholder="Reader's next step"
                    />
                  </div>
                </div>
              </div>
            ))}
            <button
              onClick={addChapter}
              className="w-full py-4 border-2 border-dashed border-slate-200 rounded-xl text-slate-400 hover:text-blue-500 hover:border-blue-200 hover:bg-blue-50 transition-all flex items-center justify-center space-x-2 font-bold"
            >
              <FiPlus />
              <span>Add Another Chapter</span>
            </button>
          </div>
        )}
      </div>
    </ModuleLayout>
  );
};

export default ModuleTwo;